import React from "react";
import {
  OrderPriceTaxBreakdown,
  PricingBreakDown,
} from "@api/graphql/generated/generated-types";
import { PricingFinalizationControls } from "../PricingFinalizationControls";
import {
  EiaFuelRateNote,
  useEiaFuelRateByRuleId,
} from "@src/common/components/EiaFuelRateNote";
import { useCourierLocale } from "@src/common/courierLocale";

/**
 * Only what this table renders. Structural rather than the full PricingBreakDown
 * so callers can pass fragment results, which select a partial
 * surchargePricingRule and therefore do not satisfy the whole schema type.
 */
type PricingBreakDownTableItem = {
  name: string;
  amount: any;
  description?: string | null;
  surchargePricingRuleId?: string | null;
};

type Props = {
  pricingBreakDown: { items: PricingBreakDownTableItem[] };
  total: number;
  orderId?: string;
  onFinalizationChange?: () => void;
  footerText?: string;
  taxBreakDown?: OrderPriceTaxBreakdown | null;
  totalAfterTax?: number;
};

function PricingBreakDownTable({
  pricingBreakDown,
  total,
  orderId,
  onFinalizationChange,
  footerText,
  taxBreakDown,
  totalAfterTax,
}: Props) {
  const { currencyDisplay } = useCourierLocale();
  // Dedicated query, skipped when orderId is absent (e.g. quote previews).
  const eiaFuelRateByRuleId = useEiaFuelRateByRuleId(orderId);
  return (
    <div className="px-1">
      <table className="w-full border-collapse">
        <tbody>
          {pricingBreakDown.items.map((item, index) => (
            <tr key={index}>
              <td className="py-2">
                <div className="flex flex-col">
                  <p>{item.name}</p>
                </div>
                {item.description && (
                  <div className="text-slate-400">
                    <p>{item.description}</p>
                  </div>
                )}
                <EiaFuelRateNote
                  eiaFuelRate={
                    item.surchargePricingRuleId
                      ? eiaFuelRateByRuleId?.[item.surchargePricingRuleId]
                      : undefined
                  }
                />
              </td>
              <td className="py-2">{currencyDisplay}{item.amount}</td>
            </tr>
          ))}
          <tr key={"total"}>
            <td className="py-4">
              <div className="font-semibold">Total</div>
            </td>
            <td className="font-semibold py-4">{currencyDisplay}{total}</td>
          </tr>
          {taxBreakDown?.taxItems && taxBreakDown.taxItems.length > 0 && (
            <>
              <tr className="text-sm text-slate-400">
                <td className="py-1">Total Before Tax</td>
                <td className="py-1">{currencyDisplay}{total}</td>
              </tr>
              {taxBreakDown.taxItems.map((item, index) => (
                <tr key={`tax-${index}`} className="text-sm text-slate-400">
                  <td className="py-1">{item.name}</td>
                  <td className="py-1">{currencyDisplay}{item.amount}</td>
                </tr>
              ))}
              <tr className="text-sm text-slate-500">
                <td className="py-2">Total After Tax</td>
                <td className="py-2">{currencyDisplay}{totalAfterTax}</td>
              </tr>
            </>
          )}
        </tbody>
      </table>

      {footerText ? (
        <p className="text-xs text-slate-400 italic mt-4">{footerText}</p>
      ) : (
        <div className="flex justify-between items-center mt-4">
          <PricingFinalizationControls
            isFinalized={true}
            orderId={orderId || ""}
            onFinalizationChange={onFinalizationChange}
          />
          <p className="text-xs text-slate-400 italic">
            The order price has been finalized.
          </p>
        </div>
      )}
    </div>
  );
}

export default PricingBreakDownTable;
